import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DialogFooter } from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { format } from 'date-fns';
import type { MutualFund } from '@/types/mutualFund';

const navFormSchema = z.object({
  investment_account_id: z.string().min(1, 'Fund is required'),
  nav_per_unit: z.number().positive('NAV must be greater than 0'),
  nav_date: z.string().min(1, 'Date is required'),
});

type NavFormSchema = z.infer<typeof navFormSchema>;

interface MutualFundNavFormProps {
  funds: MutualFund[];
  defaultFundId?: string;
  onSave: (data: NavFormSchema) => void | Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

/**
 * MutualFundNavForm allows recording a new NAV price for a selected fund.
 * Upserts on (fund, date) so re-recording the same day updates the price.
 */
export function MutualFundNavForm({
  funds,
  defaultFundId,
  onSave,
  onCancel,
  isSubmitting = false,
}: MutualFundNavFormProps) {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting: formIsSubmitting },
  } = useForm<NavFormSchema>({
    resolver: zodResolver(navFormSchema),
    defaultValues: {
      investment_account_id: defaultFundId ?? '',
      nav_per_unit: 0,
      nav_date: format(new Date(), 'yyyy-MM-dd'),
    },
  });

  const submitting = isSubmitting || formIsSubmitting;

  return (
    <form onSubmit={handleSubmit(onSave)} className="space-y-4" noValidate>
      <div className="space-y-1">
        <Label htmlFor="nav_fund">Fund *</Label>
        <Select
          value={watch('investment_account_id')}
          onValueChange={(v) => setValue('investment_account_id', v)}
        >
          <SelectTrigger id="nav_fund">
            <SelectValue placeholder="Select fund" />
          </SelectTrigger>
          <SelectContent>
            {funds.map((f) => (
              <SelectItem key={f.id} value={f.id}>
                {f.fund_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.investment_account_id && (
          <p className="text-sm text-destructive">{errors.investment_account_id.message}</p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="nav_per_unit">NAV per Unit *</Label>
          <Input
            id="nav_per_unit"
            type="number"
            step="0.000001"
            min="0"
            {...register('nav_per_unit', { valueAsNumber: true })}
          />
          {errors.nav_per_unit && (
            <p className="text-sm text-destructive">{errors.nav_per_unit.message}</p>
          )}
        </div>

        <div className="space-y-1">
          <Label htmlFor="nav_date">Date *</Label>
          <Input id="nav_date" type="date" {...register('nav_date')} />
          {errors.nav_date && (
            <p className="text-sm text-destructive">{errors.nav_date.message}</p>
          )}
        </div>
      </div>

      <DialogFooter className="pt-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : 'Record NAV'}
        </Button>
      </DialogFooter>
    </form>
  );
}
